import React, { useEffect, useState } from "react"
import toast from "react-hot-toast"
import "flatpickr/dist/themes/material_blue.css"
import FlatPickr from "react-flatpickr"
import {
  Button,
  CardTitle,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import calendar from "../../../assets/images/icons/calendar.svg"
import { useCounterOfferMutation } from "store/api/investorapi/icds.api"
import Spinners from "components/Common/Spinner"
import { interestFrequency, repaymentSchedule } from "helpers/utils"
import {
  formatAmount,
  getDateOfRepayment,
  getFundRequirementDate,
  sanitizeIntegerInputReactHookForm,
} from "helpers/common"
import { useIcdCounterOfferMutation } from "store/api/icds/icds.api"
import { Controller, useForm } from "react-hook-form"
import { joiResolver } from "@hookform/resolvers/joi"
import Joi from "joi"
import { useSelector } from "react-redux"
import moment from "moment"
import { useGetDocSignersQuery } from "store/api/signers/signers.api"

const defaultValues = {
  amount: "",
  tenure: "",
  repayment_date: "",
  interest_rate: "",
  fund_requirement_date: "",
  interest_frequency: "",
  repayment_schedule: "SINGLE",
  repayment_terms: "",
  comments: "",
}

const minDate = new Date()
minDate.setDate(minDate.getDate() + 7)

// const maxDate = new Date()
// maxDate.setDate(maxDate.getDate() + 365)
// minDate.setHours(0, 0, 0, 0)
// maxDate.setHours(23, 59, 59, 999)

function CounterFundRequestModal({
  from = "INVESTOR",
  open,
  setOpen,
  data,
  onSuccess,
  setCancelRequestOpen,
}) {
  const { user, userType } = useSelector((state) => state.user)
  const [counterOffer, { isLoading }] = useCounterOfferMutation()
  const [counterOfferAdmin, { isLoading: isOffering }] =
    useIcdCounterOfferMutation()
  const [params, setParams] = useState(defaultValues)
  const { data: docSigners } = useGetDocSignersQuery()

  useEffect(() => {
    setParams(defaultValues)
  }, [data])

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...params,
      ...(from === "ADMIN" ? { notice_period: "0" } : {}),
    },
    resolver: joiResolver(Joi.object({}).unknown(true)),
    mode: "onChange",
    reValidateMode: "onChange",
  })

  const repaymentDate = watch("repayment_date")
  const fundRequirementDate = watch("fund_requirement_date")

  useEffect(() => {
    if (repaymentDate && fundRequirementDate) {
      const repayment = moment(repaymentDate).startOf("day")
      const fundReq = moment(fundRequirementDate).startOf("day")
      const tenureInDays = repayment.diff(fundReq, "days")

      setValue("tenure", tenureInDays.toString(), { shouldValidate: true })
      if (from === "ADMIN") {
        setValue("lockin_period", tenureInDays.toString(), {
          shouldValidate: true,
        })
      }
    }
  }, [repaymentDate, fundRequirementDate, setValue])

  const repaymentScheduleValue = watch("repayment_schedule")
  const onFormSubmit = async (values) => {
    try {
      const remainingAmount = parseFloat(
        data?.amount - data?.total_invested_amount
      )
      if (from === "INVESTOR" && parseFloat(values.amount) > remainingAmount) {
        toast.error(
          `You can not counter more than ₹ ${formatAmount(remainingAmount)}`,
          {
            id: "toastMessage",
          }
        )
        return
      }
      const params = { ...values, id: data.id }

      const { success, message } =
        from === "INVESTOR"
          ? await counterOffer(params).unwrap()
          : await counterOfferAdmin(params).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        reset({ ...defaultValues })
        onSuccess()
      }
    } catch (error) {
      if (error.status === 409) {
        setCancelRequestOpen(true)
      }

      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        // setError(errors)
        Object.entries(errors).forEach(([field, message]) => {
          setError(field, { type: "server", message })
        })
      }

      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  return (
    <Modal
      isOpen={open}
      toggle={() => {
        setOpen((prev) => !prev)
      }}
      size="lg"
      centered={true}
    >
      <ModalHeader
        toggle={() => {
          setOpen((prev) => !prev)
        }}
      >
        Request Summary
      </ModalHeader>
      <ModalBody>
        <Col xl={12}>
          <Row className="mb-2">
            <Col xl={6}>
              <Label className="col-form-label">Investment Amount</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                id="horizontal-firstname-Input"
                placeholder="Investment Amount"
                value={`₹ ${formatAmount(data?.amount)}`}
              />
            </Col>
            <Col xl={6}>
              <Label className="col-form-label">Date of Repayment</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                id="horizontal-email-id "
                placeholder="Date of Repayment"
                value={getDateOfRepayment(data?.repayment_date)}
              />
            </Col>
            <Col xl={6}>
              <Label className="col-form-label">Tenure(in days)</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                placeholder="Tenure"
                value={data?.tenure}
              />
            </Col>
            <Col xl={6}>
              <Label className="col-form-label">Interest Rate (%)</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                placeholder="Interest Rate"
                value={data?.interest_rate}
              />
            </Col>

            <Col xl={6}>
              <Label className="col-form-label">
                Interest Payment Frequency
              </Label>
              <Input
                disabled
                type="text"
                className="form-control"
                placeholder="Interest Payment Frequency"
                value={
                  data &&
                  interestFrequency.find(
                    (item) => item.key === data?.interest_frequency
                  ).label
                }
              />
            </Col>

            <Col xl={6}>
              <Label className="col-form-label">Fund Requirement Date</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                id="horizontal-email-id "
                placeholder="Fund Availability Date"
                value={
                  data && getFundRequirementDate(data.fund_requirement_date)
                }
              />
            </Col>
            <Col xl={6}>
              <Label className="col-form-label">Repayment Schedule</Label>
              <Input
                disabled
                type="text"
                className="form-control"
                id="horizontal-email-id "
                placeholder="Fund Requirement Date"
                value={
                  data &&
                  repaymentSchedule.find(
                    (item) => item.key === data?.repayment_schedule
                  ).label
                }
              />
            </Col>
            {data?.repayment_schedule === "MULTIPLE" && (
              <Col xl={6}>
                <Label className="col-form-label">No. Of Installments</Label>
                <Input
                  name="repayment_terms"
                  type="text"
                  disabled
                  className="form-control"
                  id="repayment_terms"
                  placeholder="Total Installments"
                  value={data.repayment_terms}
                />
              </Col>
            )}
            {data?.created_by === "ADMIN" && (
              <>
                <Col xl={6}>
                  <Label className="col-form-label">Lock-in (in days)</Label>
                  <Input
                    name="lockin_period"
                    type="text"
                    disabled
                    className="form-control"
                    id="lockin_period"
                    placeholder="Lock-in"
                    value={data?.lockin_period}
                  />
                </Col>
                <Col xl={6}>
                  <Label className="col-form-label">
                    Notice Period (in days)
                  </Label>
                  <Input
                    name="notice_period"
                    type="text"
                    disabled={from !== "ADMIN"}
                    className="form-control"
                    id="notice_period"
                    placeholder="Notice Period"
                    value={data?.notice_period}
                  />
                </Col>
              </>
            )}
          </Row>
        </Col>

        <Col xl={12}>
          <CardTitle className="my-4">Counter Offer</CardTitle>
          <Form onSubmit={handleSubmit(onFormSubmit)}>
            <Row className="mb-2">
              <Col xl={6}>
                <Label for="amount" className="col-form-label">
                  Investment Amount <span className="error">*</span>
                </Label>

                <Controller
                  name="amount"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      name="amount"
                      type="text"
                      className="form-control"
                      id="amount"
                      placeholder="Investment Amount"
                      value={`₹ ${formatAmount(value)}`}
                      onChange={(e) =>
                        sanitizeIntegerInputReactHookForm(e, onChange)
                      }
                      invalid={errors.amount?.message ? true : false}
                    />
                  )}
                />
                {errors.amount?.message && (
                  <FormFeedback type="invalid">
                    {errors.amount?.message}
                  </FormFeedback>
                )}
              </Col>

              <Col xl={6}>
                <Label for="amount" className="col-form-label">
                  Date of Repayment <span className="error">*</span>
                </Label>
                <Controller
                  name="repayment_date"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FlatPickr
                      id="repayment_date"
                      name="repayment_date"
                      className={`form-control ${
                        errors.repayment_date?.message && "is-invalid"
                      }`}
                      placeholder="Select date"
                      options={{
                        dateFormat: "d M, Y",
                        minDate,
                        // maxDate,
                      }}
                      selected={value}
                      value={value}
                      onChange={(date) => {
                        setValue("repayment_date", date[0])
                        setValue("fund_requirement_date", "")
                      }}
                    />
                  )}
                />
                {errors.repayment_date?.message && (
                  <FormFeedback type="invalid">
                    {errors.repayment_date?.message}
                  </FormFeedback>
                )}
              </Col>

              <Col xl={6}>
                <Label
                  htmlFor="fund_requirement_date"
                  className="col-form-label"
                >
                  {from === "ADMIN"
                    ? "Fund Requirement Date"
                    : "Fund Availability Date"}{" "}
                  <span className="error">*</span>
                </Label>

                <Controller
                  name="fund_requirement_date"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="input-wrapper">
                      <FlatPickr
                        id="fund_requirement_date"
                        name="fund_requirement_date"
                        className={`form-select no-arrow-picker pageSize ${
                          errors.fund_requirement_date?.message && "is-invalid"
                        }`}
                        placeholder="Select date"
                        options={{
                          dateFormat: "d M, Y",
                          minDate: "today",
                          maxDate: repaymentDate
                            ? moment(repaymentDate).subtract(7, "days").toDate()
                            : undefined,
                        }}
                        selected={value}
                        value={value}
                        disabled={!repaymentDate}
                        onChange={(date) => {
                          setValue("fund_requirement_date", date[0])
                        }}
                      />
                      <img src={calendar} alt="" />
                    </div>
                  )}
                />

                {errors.fund_requirement_date?.message && (
                  <FormFeedback type="invalid">
                    {errors.fund_requirement_date?.message}
                  </FormFeedback>
                )}
              </Col>
              <Col xl={6}>
                <Label for="amount" className="col-form-label">
                  Tenure (Days) <span className="error">*</span>
                </Label>

                <Controller
                  name="tenure"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      disabled
                      name="tenure"
                      type="text"
                      className="form-control"
                      id="tenure"
                      placeholder="Tenure"
                      value={value}
                      onChange={(e) =>
                        sanitizeIntegerInputReactHookForm(e, onChange)
                      }
                      invalid={errors.tenure?.message ? true : false}
                    />
                  )}
                />
                {errors.tenure?.message && (
                  <FormFeedback type="invalid">
                    {errors.tenure?.message}
                  </FormFeedback>
                )}
                <span
                  className="text-muted"
                  style={{
                    fontStyle: "italic",
                  }}
                >
                  (Tenure = Date of Repayment - Fund Availability Date)
                </span>
              </Col>
              <Col xl={6}>
                <Label for="interest_rate" className="col-form-label">
                  Interest Rate (%) <span className="error">*</span>
                </Label>

                <Controller
                  name="interest_rate"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      name="interest_rate"
                      type="text"
                      className="form-control"
                      id="interest_rate"
                      placeholder="Interest Rate (%)"
                      value={value}
                      onChange={(e) =>
                        sanitizeIntegerInputReactHookForm(e, onChange)
                      }
                      invalid={errors.interest_rate?.message ? true : false}
                    />
                  )}
                />
                {errors.interest_rate?.message && (
                  <FormFeedback type="invalid">
                    {errors.interest_rate?.message}
                  </FormFeedback>
                )}
              </Col>

              <Col xl={6}>
                <Label className="col-form-label">
                  Interest Payment Frequency<span className="error">*</span>
                </Label>

                <select
                  id="interest_frequency"
                  name="interest_frequency"
                  className={`form-select pageSize ${
                    errors.interest_frequency?.message && "is-invalid"
                  }`}
                  {...register("interest_frequency")}
                >
                  <option value=""> Select Interest Payment Frequency</option>
                  {interestFrequency.map((item, index) => (
                    <option key={`interestFrequency_${index}`} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </select>

                {errors.interest_frequency?.message && (
                  <FormFeedback type="invalid">
                    {errors.interest_frequency?.message}
                  </FormFeedback>
                )}
              </Col>

              <Col xl={6}>
                <Label className="col-form-label">
                  Repayment Schedule<span className="error">*</span>
                </Label>

                <select
                  id="repayment_schedule"
                  name="repayment_schedule"
                  className={`form-select pageSize ${
                    errors.repayment_schedule?.message && "is-invalid"
                  }`}
                  {...register("repayment_schedule")}
                >
                  <option value=""> Select Repayment Schedule</option>
                  {repaymentSchedule.map((item, index) => (
                    <option key={`interestFrequency_${index}`} value={item.key}>
                      {item.label}
                    </option>
                  ))}
                </select>

                {errors.repayment_schedule?.message && (
                  <FormFeedback type="invalid">
                    {errors.repayment_schedule?.message}
                  </FormFeedback>
                )}
              </Col>

              {repaymentScheduleValue === "MULTIPLE" && (
                <Col xl={6}>
                  <Label className="col-form-label">
                    No. Of Installments <span className="error">*</span>
                  </Label>

                  <Controller
                    name="repayment_terms"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Input
                        name="repayment_terms"
                        type="text"
                        className="form-control"
                        id="repayment_terms"
                        placeholder="Total Installments"
                        value={value}
                        onChange={(e) =>
                          sanitizeIntegerInputReactHookForm(e, onChange)
                        }
                        invalid={errors.amount?.message ? true : false}
                      />
                    )}
                  />

                  {errors.repayment_terms?.message && (
                    <FormFeedback type="invalid">
                      {errors.repayment_terms?.message}
                    </FormFeedback>
                  )}
                </Col>
              )}
              {userType === "ADMIN" && (
                <>
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Lock-in (in days) <span className="error">*</span>
                    </Label>

                    <Controller
                      name="lockin_period"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Input
                          name="lockin_period"
                          type="text"
                          className="form-control"
                          id="lockin_period"
                          placeholder="Lock-in"
                          value={value}
                          // disabled
                          onChange={(e) =>
                            sanitizeIntegerInputReactHookForm(e, onChange)
                          }
                          invalid={errors.lockin_period?.message ? true : false}
                        />
                      )}
                    />

                    {errors.lockin_period?.message && (
                      <FormFeedback type="invalid">
                        {errors.lockin_period?.message}
                      </FormFeedback>
                    )}
                  </Col>
                  <Col xl={6}>
                    <Label className="col-form-label">
                      Notice Period (in days) <span className="error">*</span>
                    </Label>

                    <Controller
                      name="notice_period"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <Input
                          name="notice_period"
                          type="text"
                          className="form-control"
                          id="notice_period"
                          placeholder="Notice Period"
                          value={value}
                          onChange={(e) =>
                            sanitizeIntegerInputReactHookForm(e, onChange)
                          }
                          invalid={errors.notice_period?.message ? true : false}
                        />
                      )}
                    />

                    {errors.notice_period?.message && (
                      <FormFeedback type="invalid">
                        {errors.notice_period?.message}
                      </FormFeedback>
                    )}
                  </Col>

                  <Col xl={6}>
                    <Label className="col-form-label" htmlFor="signer">
                      Select Signatory
                      <span className="error">*</span>
                    </Label>

                    <select
                      id="signer"
                      name="signer"
                      className={`form-select pageSize ${
                        errors.signer?.message && "is-invalid"
                      }`}
                      {...register("signer")}
                    >
                      <option value="">Select Signatory</option>
                      {docSigners?.data?.map((item, index) => (
                        <option
                          key={`doc_signer_${index}`}
                          value={item.phone_no}
                        >
                          {item.name} ({item.phone_no})
                        </option>
                      ))}
                    </select>

                    {errors.signer?.message && (
                      <FormFeedback type="invalid">
                        {errors.signer?.message}
                      </FormFeedback>
                    )}
                  </Col>
                </>
              )}
            </Row>

            <Row className="mb-2">
              <Col xl={12}>
                <Label className="col-form-label">
                  Comments / Notes <span>(Optional)</span>
                </Label>

                <Controller
                  name="comments"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      name="comments"
                      type="textarea"
                      className="form-control textarea"
                      id="horizontal-firstname-Input"
                      placeholder="Add reason for counter or instructions"
                      value={value}
                      onChange={onChange}
                      invalid={errors.comments?.message ? true : false}
                    />
                  )}
                />

                {errors.comments?.message && (
                  <FormFeedback type="invalid">
                    {errors.comments?.message}
                  </FormFeedback>
                )}
              </Col>
            </Row>

            <Row className="justify-content-end mt-4">
              <Col sm={12}>
                <div className="d-flex justify-content-end align-items-center gap-3">
                  <button
                    onClick={() => setOpen(false)}
                    type="button"
                    className="btn btn-cancel w-md"
                  >
                    Cancel
                  </button>
                  <Button
                    type="submit"
                    disabled={isLoading || isOffering}
                    className="btn btn-primary w-md"
                  >
                    {isLoading || isOffering ? (
                      <Spinners />
                    ) : (
                      "Submit Counter Offer"
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Col>
      </ModalBody>
    </Modal>
  )
}

export default CounterFundRequestModal
